import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Editorial header matching the homepage. activeSection props are kept for
// backwards compatibility with existing call sites but are no longer used —
// the nav is route-based now.
interface HeaderProps {
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

const Header: React.FC<HeaderProps> = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Show the Dashboard variant only after auth is fully verified server-side.
  // Until then, treat the visitor as logged out so a stale localStorage
  // session doesn't briefly flash a Dashboard button to a signed-out user.
  const isVerifiedSignedIn = Boolean(user) && !loading;

  const goToWaitlist = () => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#waitlist');
    }
  };

  // Defensive: even though the Dashboard button only renders when `user` is
  // truthy, the auth context can briefly carry a stale or expired session.
  // Route to /auth when there's no signed-in user so the button is never a
  // dead-end click.
  const goToDashboard = () => {
    setMobileOpen(false);
    navigate(user ? '/dashboard' : '/auth');
  };

  const navLinks: Array<{ to: string; label: string }> = [
    { to: '/features', label: 'Features' },
    { to: '/how-it-works', label: 'How It Works' },
    { to: '/about', label: 'About' },
  ];

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
        <Link to="/" className="text-sm sm:text-base font-semibold tracking-tight shrink-0">
          SocialScoop
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <button
            onClick={goToWaitlist}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Join Beta
          </button>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {isVerifiedSignedIn ? (
            <Button variant="outline" size="sm" onClick={goToDashboard}>
              Dashboard
            </Button>
          ) : (
            <>
              <Link
                to="/auth"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Sign in
              </Link>
              <Button size="sm" onClick={goToWaitlist}>
                Join the Beta
              </Button>
            </>
          )}
        </div>

        <div className="md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[360px]">
              <div className="flex flex-col mt-8">
                <div className="flex flex-col">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className="py-3 text-base text-foreground hover:text-foreground/70 transition-colors border-b border-border/40"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <button
                    onClick={goToWaitlist}
                    className="py-3 text-base text-left text-foreground hover:text-foreground/70 transition-colors border-b border-border/40"
                  >
                    Join Beta
                  </button>
                </div>

                <div className="mt-6 space-y-2">
                  {isVerifiedSignedIn ? (
                    <Button variant="outline" className="w-full" onClick={goToDashboard}>
                      Dashboard
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        className="w-full"
                        onClick={() => {
                          setMobileOpen(false);
                          navigate('/auth');
                        }}
                      >
                        Sign in
                      </Button>
                      <Button className="w-full" onClick={goToWaitlist}>
                        Join the Beta
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
